import { AxiosResponse } from 'axios';
import { Company, CompanyForRequest } from 'entities/Company/types';
import { ECoUserPermission } from 'permissions';
import { User, UserForRequest } from 'entities/User/types';

export type AuthResponse = {
	accessToken: string;
	user: User;
	company: Company | null;
	permissions: ECoUserPermission[];
};

// Check User
type CheckUserReq = string;
type CheckUserRes = { success: boolean };
export type CheckUserRequest = (email: CheckUserReq) => Promise<AxiosResponse<CheckUserRes>>;

// Sign Up
type SignUpReq = {
	user: UserForRequest;
	company: CompanyForRequest;
};
type SignUpRes = AuthResponse;
export type SignUpRequest = (data: SignUpReq) => Promise<AxiosResponse<SignUpRes>>;

// Send Confirmation Email
type SendConfirmationEmailReq = { email: User['email'] | undefined };
type SendConfirmationEmailRes = { success: boolean };
export type SendConfirmationEmailRequest = (
	data: SendConfirmationEmailReq,
) => Promise<AxiosResponse<SendConfirmationEmailRes>>;

// Confirm Email
type ConfirmEmailReq = string | undefined;
type ConfirmEmailRes = { success: boolean };
export type ConfirmEmailRequest = (token: ConfirmEmailReq) => Promise<AxiosResponse<ConfirmEmailRes>>;

// Sign Up By Invitation
type SignUpByInvitationReq = {
	firstName: string;
	lastName: string;
	token: string | undefined;
	password: string;
};
type SignUpByInvitationRes = AuthResponse;
export type SignUpByInvitationRequest = (
	data: SignUpByInvitationReq,
) => Promise<AxiosResponse<SignUpByInvitationRes>>;

// Sign In
type SignInReq = {
	email: User['email'];
	password: string;
};
type SignInRes = AuthResponse | TwoFactorChallengeResponse;
export type SignInRequest = (data: SignInReq) => Promise<AxiosResponse<SignInRes>>;

// Restore Password
type RestorePasswordReq = {
	email: User['email'];
};
type RestorePasswordRes = {
	success: boolean;
};
export type RestorePasswordRequest = (data: RestorePasswordReq) => Promise<AxiosResponse<RestorePasswordRes>>;

// Create Password
type CreatePasswordReq = {
	token: string | undefined;
	password: string;
};
type CreatePasswordRes = {
	success: boolean;
};
export type CreatePasswordRequest = (data: CreatePasswordReq) => Promise<AxiosResponse<CreatePasswordRes>>;

// Auth With Google
type AuthWithGoogleTokenReq = {
	token?: string;
};
type AuthWithGoogleTokenRes = AuthResponse;
export type AuthWithGoogleTokenRequest = (
	data: AuthWithGoogleTokenReq,
) => Promise<AxiosResponse<AuthWithGoogleTokenRes>>;

// Update Password
type UpdatePasswordReq = { password: string; currentPassword: string };
type UpdatePasswordRes = { success: boolean };
export type UpdatePasswordRequest = (data: UpdatePasswordReq) => Promise<AxiosResponse<UpdatePasswordRes>>;

// Change Email
type ChangeEmailReq = { password: string; email: string; newEmail: string };
type ChangeEmailRes = { success: boolean };
export type ChangeEmailRequest = (data: ChangeEmailReq) => Promise<AxiosResponse<ChangeEmailRes>>;

export enum EAuthStep {
	TWO_FACTOR_SETUP = '2fa_setup',
	TWO_FACTOR_VERIFY = '2fa_verify',
}

export type TwoFactorChallengeResponse = {
	step: EAuthStep;
	twoFactorToken: string;
	qrCode?: string;
	secret?: string;
};

export const isTwoFactorChallenge = (
	res: AuthResponse | TwoFactorChallengeResponse,
): res is TwoFactorChallengeResponse => {
	return 'step' in res && Object.values(EAuthStep).includes(res.step);
};

// Two-Factor Setup
type TwoFactorSetupReq = { twoFactorToken: string; code: string };
type TwoFactorSetupRes = { backupCodes: string[] };
export type TwoFactorSetupRequest = (data: TwoFactorSetupReq) => Promise<AxiosResponse<TwoFactorSetupRes>>;

// Two-Factor Verify
type TwoFactorVerifyReq = { twoFactorToken: string; code: string };
type TwoFactorVerifyRes = AuthResponse;
export type TwoFactorVerifyRequest = (data: TwoFactorVerifyReq) => Promise<AxiosResponse<TwoFactorVerifyRes>>;

// Two-Factor Recovery
type TwoFactorRecoveryReq = { twoFactorToken: string; recoveryCode: string };
type TwoFactorRecoveryRes = AuthResponse & { remainingBackupCodesCount?: number };
export type TwoFactorRecoveryRequest = (
	data: TwoFactorRecoveryReq,
) => Promise<AxiosResponse<TwoFactorRecoveryRes>>;
